// Developer Platform — Tab 3: integration connections (migration-service via
// /api/v1/integrations/*). Same parallel-build rule as webhooks: 404/502 on
// load renders a muted "being rolled out" state. v1 keeps connection config
// simple — no config/credentials forms; advanced setup goes through support.
import React, { useEffect, useMemo, useState } from 'react';
import { getApiErrorMessage, isEntitlementUnavailable } from '@/lib/apiError';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Download, History, Loader2, Pause, Play, Plug, Plus, Power, Table2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/features/shared/components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/features/shared/components/ui/table';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import {
  CSV_EXPORT_EMPLOYEES_KEY, REST_PULL_KEY, SFTP_INBOUND_KEY, SFTP_EXPORT_KEY, createIntegrationConnection,
  createMappingSet, downloadRunArtifact, enqueueSync, exportConnectionCsv, getConnectionSchedule,
  getIntegrationCatalog, getRunErrors, isExportConnector, isImportConnector, isNotEntitled, isNotRolledOut, UDEMY_KEY,
  listConnectionRuns, listIntegrationConnections, listMappingSets, patchIntegrationConnection, previewTransform,
  setConnectionSchedule,
  type ConnectionSchedule, type ConnectorDefinition, type FieldMapping, type IntegrationConnection,
  type RunValidationError, type SyncRun,
} from '@/services/developerService';
import { ActionRefusal } from '@/features/shared/components/ui/ActionRefusal';
import { FeatureNotEnabledState, RolloutPendingState, errMsg, fmtDateTime, inputCls, TemporarilyUnavailableState } from './common';
import { AsanaCard } from './AsanaCard';

const CONN_STATUS_BADGE: Record<string, string> = {
  active: 'bg-success-soft text-success-strong border-transparent',
  draft: 'bg-muted text-muted-foreground border-border',
  paused: 'bg-warning-soft text-warning-strong border-transparent',
  disabled: 'bg-danger-soft text-danger-strong border-transparent',
  error: 'bg-danger-soft text-danger-strong border-transparent',
};

const RUN_STATUS_BADGE: Record<string, string> = {
  completed: 'bg-success-soft text-success-strong border-transparent',
  running: 'bg-muted text-muted-foreground border-border',
  completed_with_errors: 'bg-warning-soft text-warning-strong border-transparent',
  failed: 'bg-danger-soft text-danger-strong border-transparent',
};

// ------------------------------------------------------ new connection dialog
export const NewConnectionDialog: React.FC<{
  catalog: ConnectorDefinition[];
  onCreated: (conn: IntegrationConnection) => void;
  onClose: () => void;
}> = ({ catalog, onCreated, onClose }) => {
  const { t } = useTranslation('admin');
  const [name, setName] = useState('');
  const [connectorKey, setConnectorKey] = useState<string>('');
  const [srcType, setSrcType] = useState<'url' | 'google_sheets' | 'rest'>('url');
  const [srcUrl, setSrcUrl] = useState('');
  // SFTP source fields.
  const [sftp, setSftpState] = useState({ host: '', port: '22', path: '', username: '', password: '' });
  const [saving, setSaving] = useState(false);
  // The server's refusal (e.g. "This SFTP host can't be reached from KaziPeople —
  // it must be a public address."), kept on the dialog until the user edits or retries.
  const [refusal, setRefusal] = useState<string | null>(null);
  const setSftp: typeof setSftpState = (v) => { setRefusal(null); setSftpState(v); };
  const isRest = connectorKey === REST_PULL_KEY;
  const isSftp = connectorKey === SFTP_INBOUND_KEY || connectorKey === SFTP_EXPORT_KEY;
  const isSftpExport = connectorKey === SFTP_EXPORT_KEY;

  // Tenants pick from the public catalog only — internal (platform-managed)
  // connectors show elsewhere.
  const grouped = useMemo(() => {
    const byCategory = new Map<string, ConnectorDefinition[]>();
    catalog
      // Asana connects through its own OAuth card; Udemy installs through the
      // Marketplace's schema-driven form — neither fits this generic dialog.
      .filter((d) => d.status !== 'internal' && d.key !== 'asana' && d.key !== UDEMY_KEY)
      .forEach((d) => {
        const list = byCategory.get(d.category) || [];
        list.push(d);
        byCategory.set(d.category, list);
      });
    return Array.from(byCategory.entries());
  }, [catalog]);

  const submit = async () => {
    if (!connectorKey) {
      toast.error(t('developer.int_connector_required', { defaultValue: 'Pick a connector from the catalog.' }));
      return;
    }
    if (!name.trim()) {
      toast.error(t('developer.int_name_required', { defaultValue: 'Give the connection a name.' }));
      return;
    }
    if (isRest && !srcUrl.trim()) {
      toast.error(t('developer.int_source_required', { defaultValue: 'Enter the source URL to import from.' }));
      return;
    }
    if (isSftp && (!sftp.host.trim() || !sftp.path.trim() || !sftp.username.trim())) {
      toast.error(t('developer.int_sftp_required', { defaultValue: 'Enter the SFTP host, path and username.' }));
      return;
    }
    setSaving(true);
    setRefusal(null);
    try {
      let config: Record<string, any> | undefined;
      let credentials: Record<string, any> | undefined;
      if (isRest) {
        config = { connector: srcType, url: srcUrl.trim(), target_key: 'employees' };
      } else if (isSftp) {
        config = { host: sftp.host.trim(), port: sftp.port.trim() || '22', path: sftp.path.trim() };
        if (!isSftpExport) config.target_key = 'employees'; // import needs a target; export is the roster
        credentials = { username: sftp.username.trim(), password: sftp.password };
      }
      const conn = await createIntegrationConnection({ name: name.trim(), connector_key: connectorKey, config, credentials });
      toast.success(t('developer.int_created', { defaultValue: 'Connection created.' }));
      onCreated(conn);
    } catch (e: any) {
      setRefusal(getApiErrorMessage(e, errMsg(e, t('developer.int_create_failed', { defaultValue: 'Could not create the connection.' }))));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('developer.int_new', { defaultValue: 'New connection' })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="int-name">
              {t('developer.int_name', { defaultValue: 'Connection name' })}
            </label>
            <input
              id="int-name"
              className={inputCls}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('developer.int_name_ph', { defaultValue: 'e.g. Monthly roster export' })}
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1.5">
              {t('developer.int_connector', { defaultValue: 'Connector' })}
            </p>
            {grouped.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t('developer.int_catalog_empty', { defaultValue: 'No connectors are available yet.' })}
              </p>
            ) : (
              <div className="space-y-3">
                {grouped.map(([category, defs]) => (
                  <div key={category}>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      {category.replace(/_/g, ' ')}
                    </p>
                    <div className="space-y-1.5">
                      {defs.map((d) => (
                        <label
                          key={d.key}
                          className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                            connectorKey === d.key
                              ? 'border-orange-400 bg-orange-50 dark:bg-orange-500/[0.08]'
                              : 'border-border hover:bg-muted/40'
                          }`}
                        >
                          <input
                            type="radio"
                            name="int-connector"
                            className="w-4 h-4 mt-0.5 accent-orange-500 shrink-0"
                            checked={connectorKey === d.key}
                            onChange={() => setConnectorKey(d.key)}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5">
                              <span className="text-sm font-medium text-foreground">{d.name}</span>
                              {d.status === 'beta' && (
                                <Badge className="border bg-muted text-muted-foreground border-border text-[10px] px-1.5 py-0">
                                  {t('developer.int_beta', { defaultValue: 'Beta' })}
                                </Badge>
                              )}
                            </span>
                            <span className="block text-xs text-muted-foreground">
                              {d.direction} · {d.transport}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {isRest && (
            <div className="space-y-3 rounded-lg border border-border p-3">
              <p className="text-xs font-semibold text-foreground">
                {t('developer.int_source', { defaultValue: 'Import source (employees)' })}
              </p>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">{t('developer.int_source_type', { defaultValue: 'Source type' })}</label>
                <select className={inputCls} value={srcType} onChange={(e) => setSrcType(e.target.value as any)}>
                  <option value="url">{t('developer.src_url', { defaultValue: 'Hosted CSV (URL)' })}</option>
                  <option value="google_sheets">{t('developer.src_sheets', { defaultValue: 'Google Sheet (published)' })}</option>
                  <option value="rest">{t('developer.src_rest', { defaultValue: 'REST endpoint (JSON array)' })}</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">{t('developer.int_source_url', { defaultValue: 'Source URL' })}</label>
                <input className={inputCls} value={srcUrl} onChange={(e) => { setSrcUrl(e.target.value); setRefusal(null); }}
                  placeholder="https://…/roster.csv" />
              </div>
              <p className="text-[11px] text-muted-foreground">
                {t('developer.int_source_note', { defaultValue: 'After creating, set the field mapping (source → KaziPeople) and a schedule on the connection row.' })}
              </p>
            </div>
          )}
          {isSftp && (
            <div className="space-y-3 rounded-lg border border-border p-3">
              <p className="text-xs font-semibold text-foreground">
                {isSftpExport
                  ? t('developer.int_sftp_dest', { defaultValue: 'SFTP destination (roster push)' })
                  : t('developer.int_sftp', { defaultValue: 'SFTP source (employees)' })}
              </p>
              <div className="flex gap-2">
                <div className="flex-1 space-y-1">
                  <label className="text-xs text-muted-foreground">{t('developer.sftp_host', { defaultValue: 'Host' })}</label>
                  <input className={inputCls} value={sftp.host} onChange={(e) => setSftp((s) => ({ ...s, host: e.target.value }))} placeholder="sftp.acme.com" />
                </div>
                <div className="w-20 space-y-1">
                  <label className="text-xs text-muted-foreground">{t('developer.sftp_port', { defaultValue: 'Port' })}</label>
                  <input className={inputCls} value={sftp.port} onChange={(e) => setSftp((s) => ({ ...s, port: e.target.value }))} placeholder="22" />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  {isSftpExport ? t('developer.sftp_dest_path', { defaultValue: 'Destination file path' }) : t('developer.sftp_path', { defaultValue: 'File path' })}
                </label>
                <input className={inputCls} value={sftp.path} onChange={(e) => setSftp((s) => ({ ...s, path: e.target.value }))}
                  placeholder={isSftpExport ? '/in/kazipeople-roster.csv' : '/out/roster.csv'} />
              </div>
              <div className="flex gap-2">
                <div className="flex-1 space-y-1">
                  <label className="text-xs text-muted-foreground">{t('developer.sftp_user', { defaultValue: 'Username' })}</label>
                  <input className={inputCls} value={sftp.username} onChange={(e) => setSftp((s) => ({ ...s, username: e.target.value }))} autoComplete="off" />
                </div>
                <div className="flex-1 space-y-1">
                  <label className="text-xs text-muted-foreground">{t('developer.sftp_pass', { defaultValue: 'Password' })}</label>
                  <input type="password" className={inputCls} value={sftp.password} onChange={(e) => setSftp((s) => ({ ...s, password: e.target.value }))} autoComplete="new-password" />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {isSftpExport
                  ? t('developer.int_sftp_dest_note', { defaultValue: 'We push the employee roster CSV to this SFTP path on the schedule you set. Credentials are encrypted at rest.' })
                  : t('developer.int_sftp_note', { defaultValue: 'We poll this SFTP path on the schedule you set. Credentials are encrypted at rest. Then set the field mapping on the connection row.' })}
              </p>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            {t('developer.int_config_note', { defaultValue: 'Advanced configuration and credentials are set up with KaziPeople support after the connection is created.' })}
          </p>
          <ActionRefusal message={refusal} onDismiss={() => setRefusal(null)} testId="integration-create-refusal" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('developer.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={submit} disabled={saving || grouped.length === 0}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {t('developer.int_create', { defaultValue: 'Create connection' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --------------------------------------------------------------- runs drawer
const RunsSheet: React.FC<{
  conn: IntegrationConnection;
  onClose: () => void;
}> = ({ conn, onClose }) => {
  const { t } = useTranslation('admin');
  const [runs, setRuns] = useState<SyncRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [errorsFor, setErrorsFor] = useState<string | null>(null);
  const [runErrors, setRunErrors] = useState<RunValidationError[] | null>(null);
  const [loadingErrors, setLoadingErrors] = useState(false);

  const toggleErrors = async (runId: string) => {
    if (errorsFor === runId) { setErrorsFor(null); setRunErrors(null); return; }
    setErrorsFor(runId);
    setRunErrors(null);
    setLoadingErrors(true);
    try {
      setRunErrors(await getRunErrors(conn.id, runId));
    } catch {
      setRunErrors([]);
    } finally {
      setLoadingErrors(false);
    }
  };

  useEffect(() => {
    let alive = true;
    listConnectionRuns(conn.id)
      .then((list) => { if (alive) setRuns(list); })
      .catch(() => { if (alive) setRuns([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [conn.id]);

  const runStatusLabel = (s: string) => {
    switch (s) {
      case 'completed': return t('developer.run_completed', { defaultValue: 'Completed' });
      case 'running': return t('developer.run_running', { defaultValue: 'Running' });
      case 'completed_with_errors': return t('developer.run_with_errors', { defaultValue: 'Completed with errors' });
      case 'failed': return t('developer.run_failed', { defaultValue: 'Failed' });
      default: return s;
    }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="sm:max-w-xl w-full overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('developer.int_runs_title', { defaultValue: 'Sync runs' })}</SheetTitle>
          <SheetDescription>{conn.name}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="py-10 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              {t('developer.int_runs_loading', { defaultValue: 'Loading runs…' })}
            </div>
          ) : runs.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {t('developer.int_no_runs', { defaultValue: 'No runs yet for this connection.' })}
            </p>
          ) : runs.map((r) => (
            <div key={r.id} className="rounded-lg border border-border p-3 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground capitalize">
                  {r.trigger === 'schedule'
                    ? t('developer.run_trigger_schedule', { defaultValue: 'Scheduled' })
                    : r.trigger === 'api'
                      ? t('developer.run_trigger_api', { defaultValue: 'API' })
                      : t('developer.run_trigger_manual', { defaultValue: 'Manual' })}
                </span>
                <Badge className={`border shrink-0 ${RUN_STATUS_BADGE[r.status] || RUN_STATUS_BADGE.running}`}>
                  {runStatusLabel(r.status)}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('developer.run_rows', {
                  defaultValue: '{{ok}} ok / {{failed}} failed of {{total}} rows',
                  ok: r.rows_ok ?? 0, failed: r.rows_failed ?? 0, total: r.rows_total ?? 0,
                })}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {fmtDateTime(r.started_at)}
                {r.finished_at ? ` → ${fmtDateTime(r.finished_at)}` : ''}
              </p>
              {r.error && (
                <p className="text-[11px] text-danger break-words">{r.error}</p>
              )}
              {(r.rows_failed ?? 0) > 0 && (
                <div>
                  <Button variant="ghost" size="sm" className="h-7 px-1.5 gap-1 text-xs text-muted-foreground"
                    onClick={() => toggleErrors(r.id)}>
                    {errorsFor === r.id
                      ? t('developer.run_hide_errors', { defaultValue: 'Hide row errors' })
                      : t('developer.run_view_errors', { defaultValue: 'View {{n}} row errors', n: r.rows_failed })}
                  </Button>
                  {errorsFor === r.id && (
                    <div className="mt-1 rounded border border-border bg-muted/40 p-2 max-h-48 overflow-y-auto space-y-1">
                      {loadingErrors ? (
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                      ) : !runErrors || runErrors.length === 0 ? (
                        <p className="text-[11px] text-muted-foreground">{t('developer.run_no_row_errors', { defaultValue: 'No per-row detail recorded.' })}</p>
                      ) : runErrors.map((ve, idx) => (
                        <div key={idx} className="text-[11px] flex gap-2">
                          <span className="text-muted-foreground shrink-0">row {ve.row_no}{ve.field ? ` · ${ve.field}` : ''}</span>
                          <span className="text-danger break-words">{ve.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {(r.status === 'completed' || r.status === 'completed_with_errors') && (r.rows_total ?? 0) > 0 && (
                <Button
                  variant="outline" size="sm" className="mt-1 h-7 gap-1.5 text-xs"
                  disabled={downloadingId === r.id}
                  onClick={async () => {
                    setDownloadingId(r.id);
                    try {
                      const blob = await downloadRunArtifact(conn.id, r.id);
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${conn.name.replace(/\s+/g, '-')}-${(r.finished_at || r.started_at || '').slice(0, 10)}.csv`;
                      document.body.appendChild(a); a.click(); a.remove();
                      URL.revokeObjectURL(url);
                    } catch (e: any) {
                      toast.error(e?.response?.status === 404
                        ? t('developer.run_no_file', { defaultValue: 'No file was produced by this run.' })
                        : errMsg(e, t('developer.run_download_failed', { defaultValue: 'Could not download the file.' })));
                    } finally {
                      setDownloadingId(null);
                    }
                  }}
                >
                  <Download className="w-3.5 h-3.5" />
                  {downloadingId === r.id
                    ? t('developer.run_downloading', { defaultValue: 'Downloading…' })
                    : t('developer.run_download', { defaultValue: 'Download file' })}
                </Button>
              )}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// --------------------------------------------------------------------- tab
// L2 schedule-write: preset crons (5-field) a tenant can pick per connection.
const SCHEDULE_PRESETS: Record<string, { cron: string; enabled: boolean; label: string }> = {
  off: { cron: '', enabled: false, label: 'Off' },
  daily: { cron: '0 2 * * *', enabled: true, label: 'Daily · 02:00' },
  weekly: { cron: '0 2 * * 1', enabled: true, label: 'Weekly · Mon 02:00' },
  monthly: { cron: '0 2 1 * *', enabled: true, label: 'Monthly · 1st 02:00' },
};
function presetOf(s: ConnectionSchedule | null): string {
  if (!s || !s.enabled) return 'off';
  const hit = Object.entries(SCHEDULE_PRESETS).find(([, v]) => v.enabled && v.cron === s.cron);
  return hit ? hit[0] : 'custom';
}

const ScheduleControl: React.FC<{ conn: IntegrationConnection }> = ({ conn }) => {
  const { t } = useTranslation();
  const [sched, setSched] = useState<ConnectionSchedule | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let alive = true;
    getConnectionSchedule(conn.id).then((s) => alive && setSched(s)).catch(() => {});
    return () => { alive = false; };
  }, [conn.id]);
  const current = presetOf(sched);
  const onChange = async (key: string) => {
    const p = SCHEDULE_PRESETS[key];
    if (!p) return;
    setSaving(true);
    try {
      const s = await setConnectionSchedule(conn.id, { cron: p.cron, enabled: p.enabled });
      setSched(s);
      toast.success(p.enabled
        ? t('developer.sched_set', { defaultValue: 'Schedule updated.' })
        : t('developer.sched_off', { defaultValue: 'Automatic schedule turned off.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.sched_failed', { defaultValue: 'Could not update the schedule.' }))));
    } finally {
      setSaving(false);
    }
  };
  return (
    <select
      className={`${inputCls} h-8 py-0 text-xs w-[150px]`}
      value={current === 'custom' ? 'custom' : current}
      disabled={saving}
      onChange={(e) => onChange(e.target.value)}
    >
      {current === 'custom' && <option value="custom" disabled>{sched?.cron} (custom)</option>}
      {Object.entries(SCHEDULE_PRESETS).map(([k, v]) => (
        <option key={k} value={k}>{v.label}</option>
      ))}
    </select>
  );
};

// Canonical employee fields a source column can map to (mirrors employee-service IMPORT_FIELDS).
const EMPLOYEE_TARGETS = [
  'email', 'first_name', 'last_name', 'employee_id', 'position', 'department',
  'manager_email', 'phone', 'national_id', 'hire_date', 'status',
];

// MappingDialog — define the source→KaziPeople field mapping for an import connection (saved as the
// connection's active mapping set; the scheduled import applies it).
const MappingDialog: React.FC<{ conn: IntegrationConnection; onClose: () => void }> = ({ conn, onClose }) => {
  const { t } = useTranslation('admin');
  const [rows, setRows] = useState<FieldMapping[]>([{ source_field: '', target_field: 'email' }]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sample, setSample] = useState('');
  const [preview, setPreview] = useState<{ target: string; expr: string; output: string; error?: string }[] | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const runPreview = async () => {
    const withXform = rows.filter((r) => (r.transform || '').trim() && r.target_field.trim());
    if (!withXform.length) {
      toast.error(t('developer.map_no_transforms', { defaultValue: 'Add a transform to at least one row to preview.' }));
      return;
    }
    setPreviewing(true);
    try {
      const out = await Promise.all(
        withXform.map(async (r) => {
          const res = await previewTransform(r.transform as string, [sample]);
          if (!res.valid) return { target: r.target_field, expr: r.transform as string, output: '', error: res.error };
          const first = res.results[0];
          return { target: r.target_field, expr: r.transform as string, output: first?.output ?? '', error: first?.error };
        }),
      );
      setPreview(out);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.map_preview_failed', { defaultValue: 'Could not run the preview.' }))));
    } finally {
      setPreviewing(false);
    }
  };
  useEffect(() => {
    let alive = true;
    listMappingSets(conn.id).then((sets) => {
      if (!alive) return;
      const m = sets[0]?.mappings;
      if (m && m.length) setRows(m.map((x) => ({ source_field: x.source_field, target_field: x.target_field, transform: x.transform })));
      setLoading(false);
    }).catch(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [conn.id]);
  const update = (i: number, patch: Partial<FieldMapping>) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const save = async () => {
    const clean = rows.filter((r) => r.source_field.trim() && r.target_field.trim());
    if (!clean.length) {
      toast.error(t('developer.map_need_one', { defaultValue: 'Add at least one field mapping.' }));
      return;
    }
    setSaving(true);
    try {
      await createMappingSet(conn.id, `mapping-${Date.now()}`, clean);
      toast.success(t('developer.map_saved', { defaultValue: 'Field mapping saved.' }));
      onClose();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.map_failed', { defaultValue: 'Could not save the mapping.' }))));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('developer.map_title', { defaultValue: 'Field mapping' })} — {conn.name}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          {t('developer.map_help2', { defaultValue: 'Map each source column to a KaziPeople employee field, and optionally transform the value (e.g. trim | upper, default("N/A"), date("2006-01-02","02/01/2006")). The scheduled import applies this.' })}
        </p>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-2 mt-2">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="flex-1">{t('developer.map_source', { defaultValue: 'Source column' })}</span>
              <span className="w-4" />
              <span className="flex-1">{t('developer.map_target', { defaultValue: 'KaziPeople field' })}</span>
              <span className="flex-1">{t('developer.map_transform', { defaultValue: 'Transform (optional)' })}</span>
              <span className="w-7" />
            </div>
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <input className={`${inputCls} h-9 flex-1`} value={r.source_field} placeholder="e.g. Barua pepe"
                  onChange={(e) => update(i, { source_field: e.target.value })} />
                <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                <select className={`${inputCls} h-9 flex-1`} value={r.target_field} onChange={(e) => update(i, { target_field: e.target.value })}>
                  {EMPLOYEE_TARGETS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <input className={`${inputCls} h-9 flex-1 font-mono text-xs`} value={r.transform || ''} placeholder="trim | upper"
                  onChange={(e) => update(i, { transform: e.target.value })} />
                <Button variant="ghost" size="sm" className="h-9 w-7 p-0 shrink-0"
                  onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))} aria-label="Remove">×</Button>
              </div>
            ))}
            {/* Transform preview */}
            <div className="mt-3 rounded-md border p-3 space-y-2">
              <div className="flex items-center gap-2">
                <input className={`${inputCls} h-9 flex-1`} value={sample} placeholder={t('developer.map_sample_ph', { defaultValue: 'A sample source value to preview transforms…' })}
                  onChange={(e) => setSample(e.target.value)} />
                <Button variant="outline" size="sm" className="h-9" disabled={previewing} onClick={runPreview}>
                  {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : t('developer.map_preview', { defaultValue: 'Preview' })}
                </Button>
              </div>
              {preview && (
                <div className="space-y-1">
                  {preview.map((p, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground w-28 shrink-0">{p.target}</span>
                      <code className="font-mono text-muted-foreground">{p.expr}</code>
                      <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
                      {p.error
                        ? <span className="text-danger">{p.error}</span>
                        : <code className="font-mono">{JSON.stringify(p.output)}</code>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button variant="outline" size="sm" className="h-8 gap-1 text-xs"
              onClick={() => setRows((rs) => [...rs, { source_field: '', target_field: 'first_name' }])}>
              <Plus className="w-3.5 h-3.5" /> {t('developer.map_add', { defaultValue: 'Add field' })}
            </Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('developer.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={save} disabled={saving || loading}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {t('developer.map_save', { defaultValue: 'Save mapping' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const IntegrationsTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [connections, setConnections] = useState<IntegrationConnection[]>([]);
  const [catalog, setCatalog] = useState<ConnectorDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [rolloutPending, setRolloutPending] = useState(false);
  const [notEnabledMsg, setNotEnabledMsg] = useState<string | null>(null);
  const [unavailableMsg, setUnavailableMsg] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [runsFor, setRunsFor] = useState<IntegrationConnection | null>(null);
  const [mappingFor, setMappingFor] = useState<IntegrationConnection | null>(null);

  const onRunNow = async (conn: IntegrationConnection) => {
    setBusyId(conn.id);
    try {
      await enqueueSync(conn.id);
      toast.success(t('developer.int_run_queued', { defaultValue: 'Queued — check runs in a moment.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.int_run_failed', { defaultValue: 'Could not queue the run.' }))));
    } finally {
      setBusyId(null);
    }
  };

  useEffect(() => {
    let alive = true;
    Promise.all([listIntegrationConnections(), getIntegrationCatalog().catch(() => [] as ConnectorDefinition[])])
      .then(([conns, defs]) => {
        if (!alive) return;
        setConnections(conns);
        setCatalog(defs);
      })
      .catch((e) => {
        if (!alive) return;
        if (isEntitlementUnavailable(e)) setUnavailableMsg(errMsg(e, ''));
        else if (isNotEntitled(e)) setNotEnabledMsg(errMsg(e, ''));
        else if (isNotRolledOut(e)) setRolloutPending(true);
        else setLoadError(true);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const connectorName = (key: string): string =>
    catalog.find((d) => d.key === key)?.name || key;

  const setStatus = async (conn: IntegrationConnection, status: string, opts?: { confirmDanger?: boolean }) => {
    if (opts?.confirmDanger) {
      const ok = await confirm({
        title: t('developer.int_disable_title', { defaultValue: 'Disable connection' }),
        message: t('developer.int_disable_confirm', {
          defaultValue: 'Disable “{{name}}”? Scheduled and API-triggered syncs stop until it is re-activated.', name: conn.name,
        }),
        confirmLabel: t('developer.disable', { defaultValue: 'Disable' }),
        danger: true,
      });
      if (!ok) return;
    }
    setBusyId(conn.id);
    try {
      await patchIntegrationConnection(conn.id, { status });
      setConnections((cur) => cur.map((c) => (c.id === conn.id ? { ...c, status } : c)));
      toast.success(t('developer.int_status_updated', { defaultValue: 'Connection updated.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.status_failed', { defaultValue: 'Could not change the status.' }))));
    } finally {
      setBusyId(null);
    }
  };

  const onExport = async (conn: IntegrationConnection) => {
    setBusyId(conn.id);
    try {
      const blob = await exportConnectionCsv(conn.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employees-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('developer.int_export_ok', { defaultValue: 'Export downloaded.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.int_export_failed', { defaultValue: 'Could not run the export.' }))));
    } finally {
      setBusyId(null);
    }
  };

  const statusLabel = (s: string) => {
    switch (s) {
      case 'active': return t('developer.status_active', { defaultValue: 'Active' });
      case 'draft': return t('developer.status_draft', { defaultValue: 'Draft' });
      case 'paused': return t('developer.status_paused', { defaultValue: 'Paused' });
      case 'disabled': return t('developer.status_disabled', { defaultValue: 'Disabled' });
      case 'error': return t('developer.status_error', { defaultValue: 'Error' });
      default: return s;
    }
  };

  // …/admin/developer → …/admin/migration (Migration Center handles file imports).
  const migrationPath = pathname.replace(/\/developer.*$/, '/migration');

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('developer.int_loading', { defaultValue: 'Loading integrations…' })}
      </div>
    );
  }
  if (unavailableMsg !== null) return <TemporarilyUnavailableState message={unavailableMsg} />;
  if (notEnabledMsg !== null) {
    return <FeatureNotEnabledState message={notEnabledMsg} />;
  }
  if (rolloutPending) {
    return (
      <RolloutPendingState
        message={t('developer.int_rollout', { defaultValue: 'Integration management is being rolled out to your workspace. Check back soon.' })}
      />
    );
  }
  if (loadError) {
    return (
      <div className="rounded-lg px-3 py-2 text-sm border bg-danger-soft border-danger text-danger-strong">
        {t('developer.int_load_error', { defaultValue: 'Could not load integrations.' })}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('developer.int_subtitle', {
            defaultValue: 'Connect KaziPeople to your other systems. Each connection runs syncs whose results are logged here.',
          })}
        </p>
        <Button onClick={() => setCreating(true)}>
          <Plus className="w-4 h-4 mr-1" /> {t('developer.int_new', { defaultValue: 'New connection' })}
        </Button>
      </div>

      <div className="rounded-lg px-3 py-2 text-sm border bg-muted/50 border-border text-muted-foreground">
        {t('developer.int_migration_hint', {
          defaultValue: 'Import files through the Migration Center — runs will appear here once the import is linked to a connection.',
        })}{' '}
        <button
          type="button"
          className="text-orange-600 dark:text-orange-400 hover:underline font-medium"
          onClick={() => navigate(migrationPath)}
        >
          {t('developer.int_open_migration', { defaultValue: 'Open Migration Center' })}
        </button>
      </div>

      <AsanaCard available={catalog.some((d) => d.key === 'asana')} />

      {connections.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-14 text-center text-muted-foreground">
            <Plug className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p className="text-sm">
              {t('developer.int_empty', { defaultValue: 'No connections yet. Create one from the connector catalog to get started.' })}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('developer.col_name', { defaultValue: 'Name' })}</TableHead>
                  <TableHead>{t('developer.col_connector', { defaultValue: 'Connector' })}</TableHead>
                  <TableHead>{t('developer.col_status', { defaultValue: 'Status' })}</TableHead>
                  <TableHead>{t('developer.col_last_sync', { defaultValue: 'Last sync' })}</TableHead>
                  <TableHead>{t('developer.col_last_error', { defaultValue: 'Last error' })}</TableHead>
                  <TableHead>{t('developer.col_schedule', { defaultValue: 'Schedule' })}</TableHead>
                  <TableHead className="text-right">{t('developer.col_actions', { defaultValue: 'Actions' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {connections.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <p className="text-sm font-medium text-foreground">{c.name}</p>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-mono text-muted-foreground">{connectorName(c.connector_key)}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`border ${CONN_STATUS_BADGE[c.status] || CONN_STATUS_BADGE.draft}`}>
                        {statusLabel(c.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {c.last_sync_at
                        ? fmtDateTime(c.last_sync_at)
                        : t('developer.int_never_synced', { defaultValue: 'Never' })}
                    </TableCell>
                    <TableCell>
                      {c.last_error ? (
                        <span className="text-xs text-danger block max-w-[200px] truncate" title={c.last_error}>
                          {c.last_error}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {(isExportConnector(c.connector_key) || isImportConnector(c.connector_key) || c.connector_key === UDEMY_KEY)
                        ? <ScheduleControl conn={c} />
                        : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        {c.connector_key === CSV_EXPORT_EMPLOYEES_KEY && (
                          <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={() => onExport(c)} disabled={busyId === c.id}>
                            {busyId === c.id
                              ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                              : <Download className="w-3.5 h-3.5 mr-1" />}
                            {t('developer.int_export', { defaultValue: 'Export CSV' })}
                          </Button>
                        )}
                        {c.connector_key === SFTP_EXPORT_KEY && (
                          <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={() => onRunNow(c)} disabled={busyId === c.id}>
                            {busyId === c.id
                              ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                              : <Play className="w-3.5 h-3.5 mr-1" />}
                            {t('developer.int_push_now', { defaultValue: 'Push now' })}
                          </Button>
                        )}
                        {c.connector_key === UDEMY_KEY && (
                          <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={() => onRunNow(c)} disabled={busyId === c.id}>
                            {busyId === c.id
                              ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                              : <Play className="w-3.5 h-3.5 mr-1" />}
                            {t('developer.int_sync_now', { defaultValue: 'Sync now' })}
                          </Button>
                        )}
                        {isImportConnector(c.connector_key) && (
                          <>
                            <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={() => setMappingFor(c)} disabled={busyId === c.id}
                              title={t('developer.int_mapping', { defaultValue: 'Field mapping' })}>
                              <Table2 className="w-3.5 h-3.5 mr-1" />
                              {t('developer.int_mapping', { defaultValue: 'Mapping' })}
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={() => onRunNow(c)} disabled={busyId === c.id}>
                              {busyId === c.id
                                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                                : <Play className="w-3.5 h-3.5 mr-1" />}
                              {t('developer.int_run_now', { defaultValue: 'Run now' })}
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setRunsFor(c)} disabled={busyId === c.id}
                          aria-label={t('developer.int_runs', { defaultValue: 'View runs' })} title={t('developer.int_runs', { defaultValue: 'View runs' })}>
                          <History className="w-3.5 h-3.5" />
                        </Button>
                        {c.status === 'paused' ? (
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-success hover:bg-green-50 dark:hover:bg-green-500/10"
                            onClick={() => setStatus(c, 'active')} disabled={busyId === c.id}
                            aria-label={t('developer.int_resume', { defaultValue: 'Resume' })} title={t('developer.int_resume', { defaultValue: 'Resume' })}>
                            <Play className="w-3.5 h-3.5" />
                          </Button>
                        ) : c.status !== 'disabled' && (
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                            onClick={() => setStatus(c, 'paused')} disabled={busyId === c.id}
                            aria-label={t('developer.int_pause', { defaultValue: 'Pause' })} title={t('developer.int_pause', { defaultValue: 'Pause' })}>
                            <Pause className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {c.status === 'disabled' ? (
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-xs text-success hover:bg-green-50 dark:hover:bg-green-500/10"
                            onClick={() => setStatus(c, 'active')} disabled={busyId === c.id}>
                            {t('developer.enable', { defaultValue: 'Enable' })}
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-danger hover:bg-red-50 dark:hover:bg-red-500/10"
                            onClick={() => setStatus(c, 'disabled', { confirmDanger: true })} disabled={busyId === c.id}
                            aria-label={t('developer.disable', { defaultValue: 'Disable' })} title={t('developer.disable', { defaultValue: 'Disable' })}>
                            <Power className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {creating && (
        <NewConnectionDialog
          catalog={catalog}
          onCreated={(conn) => {
            setConnections((cur) => [conn, ...cur]);
            setCreating(false);
          }}
          onClose={() => setCreating(false)}
        />
      )}
      {runsFor && <RunsSheet conn={runsFor} onClose={() => setRunsFor(null)} />}
      {mappingFor && <MappingDialog conn={mappingFor} onClose={() => setMappingFor(null)} />}
    </div>
  );
};
